import JSZip from "jszip";
import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  AlertCircle,
  AlertTriangle,
  Briefcase,
  Download,
  Filter,
  Image as ImageIcon,
  Phone,
  Upload,
  User,
} from "react-feather";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { addAuditRecord } from "../../api/auditApi";
import {
  addAndUpload,
  deleteDocumentList,
  getDocumentByOpeAndRef,
  getFile,
  updateAndUpload,
} from "../../api/documentUpload";
import {
  addEmployee,
  employeeById,
  employeeByMultipleId,
  searchEmployee,
  updateEmployee,
  validateAccountCreation,
} from "../../api/employeeApi";
import {
  getBulkDropdownData,
  getOtherRefSearchDropdown,
} from "../../api/keyvalue";
import ContactComponent from "../../components/ContactComponent";
import DocumentUpload from "../../components/DocumentUpload";
import EmployeeComponent from "../../components/EmployeeComponent";
import Grid from "../../components/Grid";
import LoadingSpinner from "../../components/LoadingSpinner";
import Modal from "../../components/Modal";
import Tab from "../../components/Tab";
import UIMessage from "../../components/UIMessage";
import Button from "../../components/input/Button";
import Dropdown from "../../components/input/Dropdown";
import Input from "../../components/input/Input";
import SearchDropdown from "../../components/input/SearchDropdown";
import SideDrawer from "../../components/input/SideDrawer";
import AlertForm from "../../components/alert/AlertForm";
import EmployeeStatusChange from "../../components/master/EmployeeStatusChange";
import {
  ActiveCellStyle,
  DateOnlyCellRenderer,
  ResizableScrollingTextRenderer,
  StatusCellStyle,
} from "../../components/renderer/StyleRenderer";
import { employeeValidation } from "../../components/validations/EmployeeCreation";
import {
  getDecoratedFileNameOptions,
  isDriverDocumentMandatory,
  validateDriverDocuments,
} from "../../components/validations/documentRequirementHelper";
import { FORMS, MODULES } from "../../const";
import { formatCsvDate } from "../../dateConfig";
import useFirstAndLastAuthorizedTab from "../../hooks/useFirstAndLastAuthorizedTab";
import useNextAndPreviousAuthority from "../../hooks/useNextAndPreviousAuthority";
import useUserCompany from "../../hooks/useUserCompany";
import { errorActions } from "../../store/error-slice";

const Employee = () => {
  const { firstViableTab, lastViableTab } = useFirstAndLastAuthorizedTab({
    module: MODULES.Master,
    section: FORMS.EMPLOYEE,
  });

  const company = useUserCompany();
  const [drawerTab, setDrawerTab] = useState(firstViableTab);

  const { next, prev } = useNextAndPreviousAuthority({
    sectionKey: "employee",
    minTab: 1,
    maxTab: 4,
    module: MODULES.Master,
    section: FORMS.EMPLOYEE,
    currentTab: drawerTab,
    authenticated: true,
  });

  const user = useSelector((state) => state.user);
  const auth = useSelector((state) => state.user.permissions[MODULES.Master]);
  const employeePermissions = auth[FORMS.EMPLOYEE.EMP.form];
  const downloadPermission = employeePermissions?.FORM?.DOWNLOAD;
  const errorState = useSelector((state) => state.error.employeeErrors);
  const dispatch = useDispatch();

  // --- UI Control States ---
  const [isSearchAccordionOpen, setIsSearchAccordionOpen] = useState(true);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [searchSubmitLoading, setSearchSubmitLoading] = useState(false);
  const [searchFetchLoading, setSearchFetchLoading] = useState(false);
  const [searchClicked, setSearchClicked] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isImageDownloading, setIsImageDownloading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [showDeleteWarning, setShowDeleteWarning] = useState(false);

  // --- Form & Search States ---
  const [editEmployee, setEditEmployee] = useState({});
  const [empStakeholder, setEmpStakeholder] = useState(null);
  const [empCategory, setEmpCategory] = useState(null);
  const [status, setStatus] = useState(null);
  const [active, setActive] = useState(null);
  const [searchFieldReset, setSearchFieldReset] = useState(false);
  const [stakeholderDisabled, setStakeholderDisabled] = useState(true);
  const [stakeholderType, setStakeholderType] = useState([]);
  const [searchRes, setSearchRes] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [files, setFiles] = useState([]);
  const [pendingSubmitEvent, setPendingSubmitEvent] = useState(null);

  // --- Dropdown Options ---
  const [typeOptions, setTypeOptions] = useState([]);
  const [titleOptions, setTitleOptions] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [activeOptions, setActiveOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [documentTypeOptions, setDocumentTypeOptions] = useState([]);
  const [fileNameOptions, setFileNameOptions] = useState([]);
  const [empCategoryOpt, setEmpCategoryOpt] = useState([]);
  const [empAuthorizeOpt, setEmpAuthorizeOpt] = useState([]);
  const [empAcvOpt, setEmpAcvOpt] = useState([]);
  const [empStatusOpt, setEmpStatusOpt] = useState([]);
  const [empActiveOpt, setEmpActiveOpt] = useState([]);
  const [specialStatusOpt, setSpecialStatusOpt] = useState([]);
  const [altActiveOpt, setAltActiveOpt] = useState([]);
  const [altStatusOpt, setAltStatusOpt] = useState([]);
  const [empInCoverageOpt, setInCoverageOpt] = useState([]);
  const [altTypeOpt, setAltTypeOpt] = useState([]);
  const [altEventOpt, setAltEventOpt] = useState([]);

  // --- Refs ---
  const gridRef = useRef(null);
  const contactRef = useRef();
  const docRef = useRef();
  const empRef = useRef();
  const alertRef = useRef();
  const [fullName, setFullName] = useState("");
  const [commonName, setCommonName] = useState("");
  const [code, setCode] = useState("");
  const [nic, setNic] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [inspectionDate, setInspectionDate] = useState("");
  const modalRef = useRef(null);
  const deleteWarningRef = useRef();

  // --- Initial Options Retrieval ---
  useEffect(() => {
    (async () => {
      setIsLoading(true);
      setIsSearchLoading(true);

      try {
        const {
          active = [],
          status = [],
          contact: contactOpt = [],
          title: titleOpt = [],
          departments: departmentsOpt = [],
          doc_type: docTypeOpt = [],
          "file_name#9": fileNameOpt = [],
          emp_category: empCategoryOpt = [],
          emp_authorized: empAuthorizedOpt = [],
          yesno: yesnoOpt = [],
          evn_type: evnTypeOpt = [],
          alert_type: alertTypeOpt = [],
          required: coverageOpt = [],
          vehicle_status: specStatusOpt = [],
        } = await getBulkDropdownData([
          { key: "active" },
          { key: "status" },
          { key: "contact" },
          { key: "title" },
          { key: "departments" },
          { key: "doc_type" },
          { key: "file_name", group: "9" },
          { key: "emp_category" },
          { key: "emp_authorized" },
          { key: "yesno" },
          { key: "evn_type" },
          { key: "alert_type" },
          { key: "required" },
          { key: "vehicle_status" },
        ]);

        setActiveOptions(active);
        setStatusOptions(status);
        setTypeOptions(contactOpt);
        setTitleOptions(titleOpt);
        setDepartmentOptions(departmentsOpt);
        setDocumentTypeOptions(docTypeOpt);
        setFileNameOptions(fileNameOpt);
        setEmpCategoryOpt(empCategoryOpt);
        setEmpAuthorizeOpt(empAuthorizedOpt);
        setEmpAcvOpt(yesnoOpt);
        setEmpActiveOpt(active);
        setEmpStatusOpt(status);
        setAltEventOpt(evnTypeOpt);
        setAltTypeOpt(alertTypeOpt);
        setAltStatusOpt(status);
        setAltActiveOpt(active);
        setInCoverageOpt(coverageOpt);
        setSpecialStatusOpt(specStatusOpt);
      } catch (err) {
        console.error("Error fetching dropdown master data:", err);
      } finally {
        setIsLoading(false);
        setIsSearchLoading(false);
      }
    })();
  }, []);

  // --- Search Handler wrapped in useCallback ---
  const handleSearchSubmit = useCallback(async (e) => {
    if (e && typeof e.preventDefault === "function") e.preventDefault();
    setIsSearchAccordionOpen(false);
    setSearchClicked(true);
    setSearchSubmitLoading(true);

    try {
      const response = await searchEmployee({
        empFullName: fullName || "",
        empCommonName: commonName || "",
        empCode: code || "",
        empStakeholder,
        empNic: nic || "",
        empCategory,
        empInspection: inspectionDate || "",
        empToDate: toDate || "",
        empFromDate: fromDate || "",
        empStatus: status,
        empActive: active,
      });

      setSearchRes(
          response
              ? response.map((emp) => ({
                ...emp,
                empLastInspection: emp.empLastInspection
                    ? formatCsvDate(emp.empLastInspection)
                    : "",
              }))
              : []
      );
    } catch (err) {
      console.error("Error searching employees:", err);
      setSearchRes([]);
    } finally {
      setSearchSubmitLoading(false);
    }
  }, [fullName, commonName, code, nic, empStakeholder, empCategory, inspectionDate, toDate, fromDate, status, active]);

  // --- Handlers ---
  const handleDrawerOpen = () => {
    setEditMode(false);
    setEditEmployee(null);
    dispatch(errorActions.clearEmployeeError());
    setIsDrawerOpen(true);
    setDrawerTab(firstViableTab);
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
    empRef.current?.resetForm();
  };

  const resetForm = () => {
    empRef.current?.resetForm();
    setEditMode(false);
    setEditEmployee(null);
  };

  const nextButtonHandler = () => {
    switch (drawerTab) {
      case 1: {
        const empErrors = empRef.current?.validateForm() || {};
        if (Object.keys(empErrors).length > 0) {
          dispatch(
              errorActions.addEmployeeErrorObject({
                form: "employeeErrors",
                errors: empErrors,
              })
          );
          toast.error("Please fill all the required fields", {
            autoClose: 5000,
            containerId: "Modal",
          });
          return;
        } else {
          dispatch(errorActions.clearEmployeeError());
        }

        const today = new Date().toISOString().split("T")[0];
        const value = empRef.current?.getData()?.empLastInspection;
        let hasOtherErrors = false;
        if (value > today) {
          dispatch(
              errorActions.addEmployeeError({
                form: "employeeErrors",
                key: "empLastInspection",
                value: "Cannot Add Future Dates in Last Inspection",
              })
          );
          toast.error("Cannot Add Future Dates in Last Inspection", {
            autoClose: 5000,
            containerId: "Main",
          });
          hasOtherErrors = true;
        }
        const conditionalErrors = empRef.current?.conditionalValidation();
        if (conditionalErrors) {
          dispatch(
              errorActions.addEmployeeError({
                form: "employeeErrors",
                key: "empStakeholder",
                value: "Stakeholder is required for Driver/Helper",
              })
          );
          toast.error("Stakeholder is required for Driver/Helper", {
            autoClose: 5000,
            containerId: "Main",
          });
          hasOtherErrors = true;
        }
        if (hasOtherErrors) return;
        dispatch(errorActions.clearEmployeeError());
        break;
      }
      case 2: {
        const isContactDetailsInvalid = contactRef.current?.validateForm();
        if (isContactDetailsInvalid) {
          dispatch(
              errorActions.addEmployeeError({
                form: "contactErrors",
                key: "mainContactError",
                value: "Should have at least one main contact detail",
              })
          );
          toast.error("There should be at least 1 main contact", {
            autoClose: 5000,
            containerId: "Main",
          });
          return;
        }
        break;
      }
      case 3: {
        const employeeData = empRef.current?.getData() || {};
        const uploadedDocs = docRef.current?.getAllDocuments?.() || docRef.current?.getData() || [];
        const docValidation = validateDriverDocuments(
            uploadedDocs,
            employeeData.empAuthorized,
            employeeData.empCategory,
            fileNameOptions,
            employeeData.empBirthDate,
            employeeData.empMedicalIssueDate
        );
        if (!docValidation.isValid) {
          dispatch(
              errorActions.addEmployeeErrorObject({
                form: "documentUploadErrors",
                errors: docValidation.errors,
              })
          );
          toast.error(
              `Please upload all mandatory documents: ${docValidation.missingDocs.join(", ")}`,
              { autoClose: 6000, containerId: "Main" }
          );
          return;
        } else {
          dispatch(
              errorActions.addEmployeeErrorObject({
                form: "documentUploadErrors",
                errors: {},
              })
          );
        }
        break;
      }
      default:
        break;
    }
    setDrawerTab(next());
  };

  const handleSubmit = async (e, bypassDeleteCheck) => {
    if (e) e.preventDefault();
    if (!bypassDeleteCheck && empRef.current?.isDeletedActive()) {
      setPendingSubmitEvent(e);
      setShowDeleteWarning(true);
      deleteWarningRef.current?.showModal();
      return;
    }

    setSubmitted(true);
    setIsSubmitLoading(true);
    dispatch(errorActions.clearEmployeeError());
    const employeeData = empRef.current?.getData() || {};
    const employeeErrors = employeeValidation(employeeData);

    if (Object.keys(employeeErrors).length > 0) {
      dispatch(errorActions.validateEmployee({ employeeData }));
      setIsSubmitLoading(false);
      toast.error("There was an error adding Employee", {
        autoClose: 5000,
        containerId: "Main",
      });
      return;
    }

    let hasOtherErrors = false;
    const today = new Date().toISOString().split("T")[0];
    const value = employeeData.empLastInspection;
    if (value > today) {
      dispatch(
          errorActions.addEmployeeError({
            form: "employeeErrors",
            key: "empLastInspection",
            value: "Cannot Add Future Dates in Last Inspection",
          })
      );
      toast.error("Cannot Add Future Dates in Last Inspection", {
        autoClose: 5000,
        containerId: "Main",
      });
      hasOtherErrors = true;
    }

    const conditionalErrors = empRef.current?.conditionalValidation();
    if (conditionalErrors) {
      dispatch(
          errorActions.addEmployeeError({
            form: "employeeErrors",
            key: "empStakeholder",
            value: "Stakeholder is required for Driver/Helper",
          })
      );
      toast.error("Stakeholder is required for Driver/Helper", {
        autoClose: 5000,
        containerId: "Main",
      });
      hasOtherErrors = true;
    }

    const empUser = employeeData.empUserEnable;
    const isContactDetailsInvalid = contactRef.current?.validateForm();
    if (isContactDetailsInvalid) {
      dispatch(
          errorActions.addEmployeeError({
            form: "contactErrors",
            key: "mainContactError",
            value: "Should have at least one main contact detail",
          })
      );
      toast.error("There should be at least 1 main contact", {
        autoClose: 5000,
        containerId: "Main",
      });
      setIsSubmitLoading(false);
      return;
    }

    if (empUser && !editMode) {
      const empEmail = employeeData.empEmail;
      if (!empEmail) {
        dispatch(
            errorActions.addEmployeeError({
              form: "employeeErrors",
              key: "empEmail",
              value: "Email is required",
            })
        );
        toast.error("Employee email is required!", {
          autoClose: 5000,
          containerId: "Main",
        });
        hasOtherErrors = true;
      } else {
        const accountStatus = await validateAccountCreation(
            employeeData.empUserName,
            empEmail,
            2
        );
        if (accountStatus === "DUPLICATE") {
          toast.error("User account not created - email already exists !", {
            autoClose: 5000,
            containerId: "Main",
          });
          hasOtherErrors = true;
        } else if (accountStatus === "EMAIL_MISMATCH") {
          toast.error(
              "User account not created - this username already exists with a different email !",
              { autoClose: 5000, containerId: "Main" }
          );
          hasOtherErrors = true;
        }
      }
    }

    const uploadedDocs =
        docRef.current?.getAllDocuments?.() || docRef.current?.getData() || [];
    const docValidation = validateDriverDocuments(
        uploadedDocs,
        employeeData.empAuthorized,
        employeeData.empCategory,
        fileNameOptions,
        employeeData.empBirthDate,
        employeeData.empMedicalIssueDate
    );
    if (!docValidation.isValid) {
      dispatch(
          errorActions.addEmployeeErrorObject({
            form: "documentUploadErrors",
            errors: docValidation.errors,
          })
      );
      toast.error(
          `Please upload all mandatory documents: ${docValidation.missingDocs.join(", ")}`,
          { autoClose: 6000, containerId: "Main" }
      );
      hasOtherErrors = true;
    } else {
      dispatch(
          errorActions.addEmployeeErrorObject({
            form: "documentUploadErrors",
            errors: {},
          })
      );
    }

    if (hasOtherErrors) {
      setIsSubmitLoading(false);
      return;
    }

    if (editMode) {
      const contactDetails = contactRef.current?.getData();
      try {
        const response = await updateEmployee({
          document: docRef.current?.getData(),
          contactDetails: contactDetails,
          employee: empRef.current?.getData(),
          alert: alertRef.current?.getData(),
          author: user.username,
        });

        if (response !== null) {
          const documents = docRef.current?.getData() || {
            created: [],
            modified: [],
            deleted: [],
          };
          let hasUploadErrors = false;

          for (let doc of documents.created || []) {
            const { file, ...rest } = doc;
            const formData = new FormData();
            formData.append("multipartFile", doc.file);
            formData.append("documentRequest", JSON.stringify(rest));
            formData.append("isImage", rest.docCategory === 1);
            const res = await addAndUpload(formData);
            if (res && res?.responseCode === "2040") {
              toast.success(res?.message, {
                autoClose: 5000,
                containerId: "Main",
              });
            } else {
              hasUploadErrors = true;
              docRef.current?.markUploadFailed(doc.id ?? doc.fileName);
              toast.error(
                  res?.message || `Error adding document ${doc.docName}`,
                  { autoClose: 5000, containerId: "Main" }
              );
            }
          }

          for (let doc of documents.modified || []) {
            const { file, ...rest } = doc;
            const formData = new FormData();
            formData.append("multipartFile", doc.file);
            formData.append("documentRequest", JSON.stringify(rest));
            formData.append("isImage", rest.docCategory === 1);
            const res = await updateAndUpload(formData);
            if (res && res?.responseCode === "2040") {
              toast.success(res?.message, {
                autoClose: 5000,
                containerId: "Main",
              });
            } else {
              hasUploadErrors = true;
              docRef.current?.markUploadFailed(doc.id ?? doc.fileName);
              toast.error(
                  res?.message || `Error updating document ${doc.docName}`,
                  { autoClose: 5000, containerId: "Main" }
              );
            }
          }

          if (documents?.deleted?.length > 0) {
            await deleteDocumentList({ docIdList: documents?.deleted });
            toast.success("Documents deleted successfully!", {
              autoClose: 5000,
              containerId: "Main",
            });
          }

          toast.success("Employee updated successfully!", {
            autoClose: 5000,
            containerId: "Main",
          });

          if (hasUploadErrors) {
            setIsSubmitLoading(false);
            return;
          }
        }
      } catch (err) {
        if (err.response?.status === 406) {
          dispatch(
              errorActions.addEmployeeError({
                form: "employeeErrors",
                key: "empNic",
                value: "employee NIC should be unique",
              })
          );
          toast.error("employee NIC should be unique!", {
            autoClose: 5000,
            containerId: "Main",
          });
        } else {
          toast.error("Error Updating Employee!", {
            autoClose: 5000,
            containerId: "Main",
          });
        }
        setIsSubmitLoading(false);
        return;
      }
    } else {
      try {
        const response = await addEmployee({
          document: docRef.current?.getData(),
          contactDetails: contactRef.current?.getData(),
          employee: empRef.current?.getData(),
          alert: alertRef.current?.getData(),
          author: user.username,
        });

        if (response?.data === "user_duplicate") {
          toast.error("User account not created - email already exists !", {
            autoClose: 5000,
            containerId: "Main",
          });
        } else if (response !== null) {
          const documents = docRef.current?.getData() || [];
          let hasUploadErrors = false;

          for (let doc of documents) {
            const { file, ...rest } = doc;
            const formData = new FormData();
            formData.append("multipartFile", doc.file);
            formData.append(
                "documentRequest",
                JSON.stringify({
                  ...rest,
                  docReference: response.data,
                })
            );
            formData.append("isImage", rest.docCategory === 1);
            const res = await addAndUpload(formData);
            if (res && res?.responseCode === "2040") {
              toast.success(res?.message, {
                autoClose: 5000,
                containerId: "Main",
              });
            } else {
              hasUploadErrors = true;
              docRef.current?.markUploadFailed(doc.id ?? doc.fileName);
            }
          }

          toast.success("Employee added successfully!", {
            autoClose: 5000,
            containerId: "Main",
          });

          if (hasUploadErrors) {
            setIsSubmitLoading(false);
            return;
          }
        }
      } catch (err) {
        toast.error("Error Adding Employee!", {
          autoClose: 5000,
          containerId: "Main",
        });
        setIsSubmitLoading(false);
        return;
      }
    }

    // --- Reload grid search data if search was already run ---
    if (searchClicked) {
      await handleSearchSubmit();
    }

    resetForm();
    handleDrawerClose();
    setIsSubmitLoading(false);
  };

  const handleSearchReset = (e) => {
    if (e) e.preventDefault();
    setFullName("");
    setCommonName("");
    setCode("");
    setNic("");
    setInspectionDate("");
    setToDate("");
    setFromDate("");

    setEmpStakeholder("");
    setEmpCategory("");
    setStatus("");
    setActive("");
    setStakeholderDisabled(true);
    setSearchFieldReset(true);

    setTimeout(() => {
      setSearchFieldReset(false);
    }, 100);
  };

  const handleSelectionChanged = useCallback(() => {
    const selectedData = gridRef.current?.getSelectedData() || [];
    setSelectedRows(selectedData);
    if (selectedData.length > 0) {
      setSelectedIds(selectedData.map((item) => item.empId));
    } else {
      setSelectedIds([]);
    }
  }, []);

  const handleDownload = async () => {
    await addAuditRecord({
      company: company ? company.comId : null,
      action: "Employee Details Download",
      params: selectedIds.join(",").toString(),
      type: 5,
      module: 2,
      user: user.username,
    });

    setIsDownloading(true);
    const response = await employeeByMultipleId(selectedIds);

    if (!response || !response.length) {
      setIsDownloading(false);
      return;
    }

    const headerMap = {
      empFullname: "Full Name",
      empCommonName: "Common Name",
      empCategoryName: "Designation",
      empStakeholder: "Stakeholder",
      empNic: "NIC",
      empLicenceNumber: "License Number",
      empExpiaryDate: "License Expiry Date",
      empPoliceExpiry: "Police Report Expiry Date",
      empGsExpiry: "Gramasewaka Report Expiry Date",
      empAuthorizedStatus: "Authorized Status",
      empBirthDate: "Date Of Birth",
      empStakeholderName: "Stakeholder Name",
      empAddress1: "Address1",
      empAddress2: "Address2",
      empZipCode: "Zip Code",
      empState: "State",
      empCity: "City",
      empCountryName: "Country",
      empPhone: "Phone",
      empMobile: "Mobile",
      empEmail: "Email",
      empStatusName: "Status",
      createdBy:"Created By",
      createdDate: "Created Date",
      empRemarks: "Remarks",
      acvValue: "ACV Verified",
      empAcvExpiry: "ACV Expiry Date",
      empLastInspection: "Last Inspection Date",
      insuranceExpDate: "Insurance Expiry Date",
    };

    const keys = Object.keys(headerMap);
    const headers = keys.map((key) => headerMap[key]);

    const csvRows = [headers.join(",")];
    response.forEach((row) => {
      const values = keys.map((key) => {
        let cell = row[key];
        const dateFields = [
          "createdDate",
          "empBirthDate",
          "empExpiaryDate",
          "empLastInspection",
          "insuranceExpDate",
          "empPoliceExpiry",
          "empGsExpiry",
          "empAcvExpiry",
        ];

        if (cell && dateFields.includes(key)) {
          cell = formatCsvDate(cell);
        }
        cell = cell ?? "";
        cell = cell.toString().replace(/"/g, '""');
        return `"${cell}"`;
      });
      csvRows.push(values.join(","));
    });

    const csvString = csvRows.join("\n");
    const blob = new Blob([csvString], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.style.display = "none";
    a.href = url;
    a.download = "Selected_Employees.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

    setSelectedIds([]);
    setIsDownloading(false);
  };

  const handleImageDownload = async () => {
    await addAuditRecord({
      company: company ? company.comId : null,
      action: "Employee Image Download",
      params: selectedRows
          .map((item) => item.empId)
          .join(",")
          .toString(),
      type: 6,
      module: 5,
      user: user.username,
    });

    setIsImageDownloading(true);
    const zip = new JSZip();
    let totalFilesAdded = 0;

    for (const employee of selectedRows) {
      const documents = await getDocumentByOpeAndRef(9, employee.empId);

      if (documents && documents.length > 0) {
        const folderName = employee.empNic || `employee-${employee.empId}`;
        const employeeFolder = zip.folder(folderName);

        for (const doc of documents) {
          const fileData = await getFile(doc.docId);
          if (fileData) {
            employeeFolder.file(doc.docName, fileData);
            totalFilesAdded++;
          }
        }
      }
    }

    if (totalFilesAdded > 0) {
      const content = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(content);
      const link = document.createElement("a");
      link.href = url;
      link.download = "Employee-Images.zip";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } else {
      toast.warn("No image documents found for selected employees.", {
        autoClose: 5000,
        containerId: "Main",
      });
    }

    setIsImageDownloading(false);
  };

  const handleStatusChange = () => {
    setIsModalOpen(true);
    modalRef.current?.showModal();
  };

  const actionsMenuChange = (data) => {
    switch (data.value) {
      case 1:
        handleDownload();
        break;
      case 2:
        handleStatusChange();
        break;
      default:
        break;
    }
  };

  return (
      <div style={{ padding: "10px" }}>
        {/* Top Header Card */}
        <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "15px",
              borderBottom: "1px solid #e0e0e0",
              paddingBottom: "10px",
            }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <Briefcase size={22} color="#2185d0" />
            <h2 style={{ margin: 0, fontSize: "1.25rem", color: "#2c3e50" }}>
              Employee Management
            </h2>
          </div>

          <Button
              template="drawerOpen"
              disabled={firstViableTab === -1}
              onClick={handleDrawerOpen}
              authenticated={true}
              form={FORMS.EMPLOYEE.EMP.form}
              module={MODULES.Master}
              editMode={false}
          />
        </div>

        {/* Search Accordion Container */}
        <div
            style={{
              backgroundColor: "#fff",
              border: "1px solid #d4d4d5",
              borderRadius: "6px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
              marginBottom: "20px",
              overflow: "hidden",
            }}
        >
          {/* Accordion Title Header */}
          <div
              onClick={() => setIsSearchAccordionOpen(!isSearchAccordionOpen)}
              style={{
                padding: "12px 20px",
                backgroundColor: "#f8f9fa",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                cursor: "pointer",
                borderBottom: isSearchAccordionOpen ? "1px solid #d4d4d5" : "none",
              }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Filter size={16} color="#4183c4" />
              <span style={{ fontWeight: "600", color: "#4183c4" }}>
              Search Filter Criteria
            </span>
            </div>
            <span style={{ color: "#888", fontSize: "0.85rem" }}>
            {isSearchAccordionOpen
                ? "▲ Collapse Criteria"
                : "▼ Expand Filter Criteria"}
          </span>
          </div>

          {/* Accordion Body Form */}
          {isSearchAccordionOpen && (
              <div style={{ padding: "20px" }}>
                {isSearchLoading ? (
                    <LoadingSpinner />
                ) : (
                    <form className="ui form">
                      <div className="six fields">
                        <Input
                            label="Full Name"
                            placeholder="Full Name"
                            name="stakeholderName"
                            value={fullName}
                            onChange={(e) => setFullName(e.target.value)}
                        />
                        <Input
                            label="Common Name"
                            placeholder="Common Name"
                            name="stakeholderCode"
                            value={commonName}
                            onChange={(e) => setCommonName(e.target.value)}
                        />
                        <Input
                            label="Code"
                            placeholder="Code"
                            name="employeeCode"
                            value={code}
                            onChange={(e) => setCode(e.target.value)}
                        />
                        <Input
                            label="NIC"
                            name="nic"
                            placeholder="e.g. 000000000V"
                            value={nic}
                            onChange={(e) => setNic(e.target.value)}
                        />
                        <Dropdown
                            label="Category"
                            options={empCategoryOpt}
                            placeholder="Category"
                            style={{ height: "20px" }}
                            value={empCategory}
                            onChange={(e, data) => {
                              setEmpCategory(data.value);
                              if (data.value) {
                                setStakeholderDisabled(false);
                                setStakeholderType(
                                    data.value === 3 || data.value === 4
                                        ? "Customer"
                                        : "Transporter"
                                );
                              } else {
                                setStakeholderDisabled(true);
                              }
                            }}
                        />
                        <SearchDropdown
                            key={`search-stakeholder-${searchFieldReset}`}
                            editMode={false}
                            reset={searchFieldReset}
                            setReset={setSearchFieldReset}
                            clearable={true}
                            onClear={() => setEmpStakeholder("")}
                            onResultSelect={(data) => setEmpStakeholder(data)}
                            api={getOtherRefSearchDropdown}
                            apiDependency={{
                              type: "stakeholder_type",
                              value: stakeholderType,
                            }}
                            label="Stakeholder"
                            disabled={stakeholderDisabled}
                        />
                      </div>

                      <div className="five fields">
                        <Input
                            label="Inspection Date"
                            type="date"
                            name="InspectionDate"
                            value={inspectionDate}
                            onChange={(e) => setInspectionDate(e.target.value)}
                        />
                        <Input
                            label="From Date"
                            type="date"
                            name="empExpiaryDate"
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                        />
                        <Input
                            label="To Date"
                            type="date"
                            name="empExpiaryDate"
                            value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                        />
                        <Dropdown
                            label="Status"
                            options={specialStatusOpt}
                            placeholder="Status"
                            style={{ height: "20px" }}
                            value={status}
                            onChange={(e, data) => setStatus(data.value)}
                        />
                        <Dropdown
                            label="Active"
                            options={activeOptions}
                            placeholder="Active"
                            style={{ height: "20px" }}
                            value={active}
                            onChange={(e, data) => setActive(data.value)}
                        />
                      </div>

                      {/* Form Action Controls */}
                      <div
                          style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: "10px",
                            marginTop: "15px",
                            paddingTop: "15px",
                            borderTop: "1px solid #eeeeee",
                          }}
                      >
                        <Button template="searchReset" onClick={handleSearchReset} />
                        <Button template="search" onClick={handleSearchSubmit} />
                      </div>
                    </form>
                )}
              </div>
          )}
        </div>

        {/* Dynamic Batch Actions Bar */}
        {selectedRows.length > 0 && downloadPermission && (
            <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "10px 15px",
                  backgroundColor: "#e8f4f8",
                  border: "1px solid #b2d8d8",
                  borderRadius: "6px",
                  marginBottom: "15px",
                }}
            >
          <span style={{ fontWeight: "600", color: "#2185d0" }}>
            {selectedRows.length} record(s) selected
          </span>
              <div style={{ display: "flex", gap: "10px" }}>
                <Button
                    isLoading={isImageDownloading}
                    Icon={ImageIcon}
                    onClick={handleImageDownload}
                    label="Download Selected Images"
                />
                <Button
                    isLoading={isDownloading}
                    Icon={Download}
                    onClick={handleDownload}
                    label="Download Selected CSV"
                />
              </div>
            </div>
        )}

        {/* Results Grid Display */}
        {searchClicked && (
            <div style={{ marginTop: "10px" }}>
              {searchSubmitLoading ? (
                  <LoadingSpinner />
              ) : (
                  <Grid
                      ref={gridRef}
                      isLoading={searchFetchLoading}
                      heading="Employees Record"
                      showDownloadButton={downloadPermission}
                      downloadParam=""
                      downloadAction="Employee Excel Download"
                      downloadOnlySelected={false}
                      excelSheetDownload={true}
                      vrScroll={true}
                      multiRowSelection={true}
                      onSelectionChanged={handleSelectionChanged}
                      rowSelection="multiple"
                      handleActionMenuChange={actionsMenuChange}
                      showActionMenu={selectedRows.length > 0}
                      actionMenuOptions={[
                        { key: 1, text: "Download Details", value: 1 },
                        { key: 2, text: "Change Status", value: 2 },
                      ]}
                      content={searchRes}
                      config={[
                        {
                          headerName: "Code",
                          field: "empCode",
                          checkboxSelection: true,
                          headerCheckboxSelection: true,
                          pinned: "left",
                          minWidth: 180,
                          width: 180,
                        },
                        {
                          headerName: "Name",
                          field: "empFullName",
                          pinned: "left",
                          cellRenderer: ResizableScrollingTextRenderer,
                        },
                        {
                          headerName: "Stakeholder",
                          field: "stkName",
                          cellRenderer: ResizableScrollingTextRenderer,
                          minWidth: 200,
                        },
                        {
                          headerName: "Category",
                          field: "employeeCategory",
                          minWidth: 150,
                        },
                        {
                          headerName: "Last Inspection Date",
                          field: "empLastInspection",
                          cellRenderer: DateOnlyCellRenderer,
                          minWidth: 180,
                        },
                        {
                          headerName: "NIC",
                          field: "empNic",
                          minWidth: 150,
                        },
                        {
                          headerName: "License Number",
                          field: "empLicenceNumber",
                          minWidth: 150,
                        },
                        {
                          headerName: "License Expiry Date",
                          field: "empExpiaryDate",
                          cellRenderer: DateOnlyCellRenderer,
                          minWidth: 180,
                        },
                        {
                          headerName: "GS Expiry Date",
                          field: "empGsExpiry",
                          cellRenderer: DateOnlyCellRenderer,
                          minWidth: 180,
                        },
                        {
                          headerName: "Police Clearance Expiry Date",
                          field: "empPoliceExpiry",
                          cellRenderer: DateOnlyCellRenderer,
                          minWidth: 220,
                        },
                        {
                          headerName: "ACV Expiry Date",
                          field: "empAcvExpiry",
                          cellRenderer: DateOnlyCellRenderer,
                          minWidth: 180,
                        },
                        {
                          headerName: "Insurance Expiry Date",
                          field: "empInsuranceExpDate",
                          cellRenderer: DateOnlyCellRenderer,
                          minWidth: 180,
                        },
                        {
                          headerName: "Insurance Provider",
                          field: "insuranceProvider",
                          minWidth: 180,
                        },
                        {
                          headerName: "Mobile Number",
                          field: "empMobile",
                          minWidth: 180,
                        },
                        {
                          headerName: "Email Address",
                          field: "empEmail",
                          cellRenderer: ResizableScrollingTextRenderer,
                          minWidth: 220,
                        },
                        {
                          headerName: "Created By",
                          field: "createdBy",
                          minWidth: 180,
                        },
                        {
                          headerName: "Created Date",
                          field: "createdDate",
                          cellRenderer: DateOnlyCellRenderer,
                          minWidth: 180,
                        },
                        {
                          headerName: "Last Modified By",
                          field: "lastModifiedBy",
                          minWidth: 180,
                        },
                        {
                          headerName: "Last Modified Date",
                          field: "lastModifiedDate",
                          cellRenderer: DateOnlyCellRenderer,
                          minWidth: 180,
                        },
                        {
                          headerName: "Status",
                          field: "statusName",
                          cellRenderer: StatusCellStyle,
                          pinned: "right",
                          minWidth: 180,
                        },
                        {
                          headerName: "Active",
                          field: "activeName",
                          cellRenderer: ActiveCellStyle,
                          pinned: "right",
                          minWidth: 180,
                        },
                      ]}
                      onCellDoubleClicked={async (props) => {
                        setSearchFetchLoading(true);
                        try {
                          const response = await employeeById(props.data.empId);
                          setEditEmployee(response);
                          dispatch(errorActions.clearEmployeeError());
                          setEditMode(true);
                          setIsDrawerOpen(true);
                        } catch (err) {
                          console.error("Error fetching employee details:", err);
                        } finally {
                          setSearchFetchLoading(false);
                        }
                      }}
                  />
              )}
            </div>
        )}

        {/* Side Drawer Component */}
        <SideDrawer
            open={isDrawerOpen}
            handleDrawerClose={handleDrawerClose}
            head={
              editMode ? (
                  <div
                      style={{
                        padding: "12px 20px",
                        borderBottom: "1px solid #e0e0e0",
                        backgroundColor: "#f8f9fa",
                      }}
                  >
              <span
                  style={{
                    fontSize: "0.8rem",
                    color: "#6c757d",
                    textTransform: "uppercase",
                  }}
              >
                Editing Employee
              </span>
                    <h3 style={{ margin: 0, color: "#212529" }}>
                      {editEmployee?.employee?.empCode}
                    </h3>
                  </div>
              ) : null
            }
            body={
              <form className="drawer-content" onSubmit={handleSubmit}>
                <div className="ui top attached tabular menu">
                  <Tab
                      Icon={User}
                      onClick={() => setDrawerTab(1)}
                      active={drawerTab === 1}
                      errors={Object.keys(errorState.employeeErrors).length !== 0}
                      label="Employee"
                      authenticated={true}
                      form={FORMS.EMPLOYEE.EMP_DETAIL.form}
                      resizeForSmallerScreens={true}
                      showMandatory={true}
                      isMandatory={true}
                  />
                  <Tab
                      Icon={Phone}
                      onClick={() => setDrawerTab(2)}
                      active={drawerTab === 2}
                      label="Contact"
                      errors={Object.keys(errorState.contactErrors).length !== 0}
                      authenticated={true}
                      form={FORMS.EMPLOYEE.EMP_CONTACT.form}
                      resizeForSmallerScreens={true}
                      showMandatory={true}
                      isMandatory={true}
                  />
                  <Tab
                      Icon={Upload}
                      onClick={() => setDrawerTab(3)}
                      active={drawerTab === 3}
                      label="Documents"
                      authenticated={true}
                      resizeForSmallerScreens={true}
                      form={FORMS.EMPLOYEE.EMP_DOC.form}
                      showMandatory={isDriverDocumentMandatory(
                          empRef.current?.getData()?.empCategory,
                          empRef.current?.getData()?.empAuthorized
                      )}
                      isMandatory={isDriverDocumentMandatory(
                          empRef.current?.getData()?.empCategory,
                          empRef.current?.getData()?.empAuthorized
                      )}
                      errors={
                          Object.keys(errorState.documentUploadErrors || {}).length !== 0
                      }
                  />
                  <Tab
                      Icon={AlertTriangle}
                      onClick={() => setDrawerTab(4)}
                      active={drawerTab === 4}
                      label="Alert"
                      authenticated={true}
                      resizeForSmallerScreens={true}
                      form={FORMS.EMPLOYEE.EMP_ALERT.form}
                  />
                </div>

                <div
                    className="ui bottom attached active tab segment"
                    style={{ display: drawerTab === 1 ? "block" : "none" }}
                >
                  <EmployeeComponent
                      ref={empRef}
                      isLoading={isLoading}
                      errors={errorState.employeeErrors}
                      dropdown={{
                        empCategoryOpt,
                        empAuthorizeOpt,
                        empAcvOpt,
                        empStatusOpt,
                        empActiveOpt,
                        empInCoverageOpt,
                        specialStatusOpt,
                      }}
                      editMode={editMode}
                      currentValues={editMode ? editEmployee?.employee : null}
                  />
                </div>

                <div
                    className="ui bottom attached active tab segment"
                    style={{ display: drawerTab === 2 ? "block" : "none" }}
                >
                  <ContactComponent
                      opeType={9}
                      ref={contactRef}
                      useDynamicFields={true}
                      type={1}
                      keyValue={"employee-type"}
                      errors={errorState.contactErrors}
                      dropdown={{
                        typeOptions,
                        titleOptions,
                        departmentOptions,
                        statusOptions,
                        activeOptions,
                      }}
                      isLoading={isLoading}
                      editMode={editMode}
                      currentValues={editMode ? editEmployee?.contactDetails : null}
                  />
                </div>

                <div
                    className="ui bottom attached active tab segment"
                    style={{ display: drawerTab === 3 ? "block" : "none" }}
                >
                  <DocumentUpload
                      opeType={9}
                      ref={docRef}
                      files={files}
                      setFiles={setFiles}
                      reference={
                        editEmployee?.employee ? editEmployee.employee.empId : null
                      }
                      dropdown={{
                        documentTypeOptions,
                        statusOptions,
                        fileNameOptions: getDecoratedFileNameOptions(fileNameOptions, {
                          opeType: 9,
                          empCategory: empRef.current?.getData()?.empCategory,
                          empAuthorized: empRef.current?.getData()?.empAuthorized,
                          empBirthDate: empRef.current?.getData()?.empBirthDate,
                        }),
                        activeOptions,
                      }}
                      isLoading={isLoading}
                      editMode={editMode}
                      currentValues={editMode ? editEmployee?.document : null}
                  />
                </div>

                <div
                    className="ui bottom attached active tab segment"
                    style={{ display: drawerTab === 4 ? "block" : "none" }}
                >
                  <AlertForm
                      opeType={9}
                      ref={alertRef}
                      errors={errorState.alertErrors}
                      dropdown={{
                        altActiveOpt,
                        altStatusOpt,
                        altTypeOpt,
                        altEventOpt,
                      }}
                      isLoading={isLoading}
                      editMode={editMode}
                      module={MODULES.Master}
                      currentValues={editMode ? editEmployee?.alert : null}
                  />
                </div>

                {/* Step Navigation Controls */}
                <div
                    style={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      marginTop: "20px",
                      paddingTop: "15px",
                      borderTop: "1px solid #eee",
                    }}
                >
                  <div style={{ display: "flex", gap: "10px" }}>
                    {drawerTab > firstViableTab && (
                        <Button
                            template="back"
                            tab={drawerTab - 1}
                            module={MODULES.Master}
                            section={FORMS.EMPLOYEE}
                            authenticated={true}
                            onClick={() => setDrawerTab(prev())}
                        />
                    )}
                    {drawerTab < lastViableTab && (
                        <Button
                            template="next"
                            tab={drawerTab + 1}
                            module={MODULES.Master}
                            section={FORMS.EMPLOYEE}
                            authenticated={true}
                            onClick={nextButtonHandler}
                        />
                    )}
                    {(editMode || drawerTab === lastViableTab) && !isLoading && (
                        <Button
                            type="submit"
                            template="create"
                            isLoading={isSubmitLoading}
                            create={!editMode}
                            form={FORMS.EMPLOYEE.EMP.form}
                            module={MODULES.Master}
                            section={FORMS.EMPLOYEE}
                            authenticated={true}
                            editMode={editMode}
                        />
                    )}
                  </div>
                </div>

                {/* Consolidated Error Message */}
                {submitted && errorState.hasErrors && (
                    <div style={{ marginTop: "15px" }}>
                      <UIMessage type="negative" width="100%" centered={false}>
                        <div className="content">
                          <div className="header" style={{ marginBottom: "5px" }}>
                            There were some errors with your submission
                          </div>
                          {Object.keys(errorState.employeeErrors).length > 0 && (
                              <div>
                                <strong>Employee Tab:</strong>
                                <ul style={{ margin: "5px 0" }}>
                                  {Object.entries(errorState.employeeErrors).map(
                                      ([key, value]) => (
                                          <li key={key}>{value}</li>
                                      )
                                  )}
                                </ul>
                              </div>
                          )}
                          {Object.keys(errorState.contactErrors).length > 0 && (
                              <div>
                                <strong>Contact Tab:</strong>
                                <ul style={{ margin: "5px 0" }}>
                                  {Object.entries(errorState.contactErrors).map(
                                      ([key, value]) => (
                                          <li key={key}>{value}</li>
                                      )
                                  )}
                                </ul>
                              </div>
                          )}
                          {Object.keys(errorState.documentUploadErrors || {}).length > 0 && (
                              <div>
                                <strong>Documents Tab:</strong>
                                <ul style={{ margin: "5px 0" }}>
                                  {Object.entries(errorState.documentUploadErrors).map(
                                      ([key, value]) => (
                                          <li key={key}>{value}</li>
                                      )
                                  )}
                                </ul>
                              </div>
                          )}
                        </div>
                      </UIMessage>
                    </div>
                )}
              </form>
            }
            style={{ width: "60vw", boxShadow: "none" }}
        />

        {/* Delete Confirmation Warning Modal */}
        <Modal ref={deleteWarningRef} closeButton={false} minWidth="320px">
          {showDeleteWarning && (
              <div style={{ padding: "20px" }}>
                <h3
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      margin: "0 0 10px 0",
                      color: "#db2828",
                    }}
                >
                  <AlertCircle color="#db2828" size={24} />
                  <span>Warning</span>
                </h3>
                <p style={{ color: "#555", fontSize: "0.95rem" }}>
                  Active is set to <strong>Deleted</strong>. Are you sure you want to
                  proceed with this action?
                </p>
                <div
                    style={{
                      display: "flex",
                      gap: "10px",
                      justifyContent: "flex-end",
                      marginTop: "20px",
                    }}
                >
                  <Button
                      label="Cancel"
                      onClick={() => {
                        deleteWarningRef.current?.close();
                        setShowDeleteWarning(false);
                        setPendingSubmitEvent(null);
                      }}
                  />
                  <Button
                      label="Proceed"
                      className="ui red button"
                      onClick={() => {
                        deleteWarningRef.current?.close();
                        setShowDeleteWarning(false);
                        handleSubmit(pendingSubmitEvent, true);
                        setPendingSubmitEvent(null);
                      }}
                  />
                </div>
              </div>
          )}
        </Modal>

        {/* Status Change Modal */}
        <Modal
            ref={modalRef}
            minWidth="60vw"
            style={{ height: "50vh" }}
            open={isModalOpen}
            onClose={() => setIsModalOpen(false)}
        >
          <EmployeeStatusChange
              empIds={selectedIds}
              closeModal={() => {
                modalRef.current?.close();
                if (searchClicked) {
                  handleSearchSubmit();
                }
              }}
          />
        </Modal>
      </div>
  );
};

export default Employee;